import { useState } from "react";
import { mergeClasses, Tooltip } from "@fluentui/react-components";
import { ChevronDoubleRightRegular } from "@fluentui/react-icons";
import { IGroupWorkspaceRowProps } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.types";
import {
  getRagDotClassName,
  useGroupedWorkspaceDashboardStyles,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.styles";
import { WorkspaceLogTabs } from "apps/project-iq/components/GroupedWorkspaceDashboard/WorkspaceLogTabs";

const getCountValueClass = (
  count: number,
  styles: ReturnType<typeof useGroupedWorkspaceDashboardStyles>,
): string =>
  mergeClasses(styles.countValue, count === 0 && styles.countValueZero);

export const GroupWorkspaceRow = ({
  rowData,
}: IGroupWorkspaceRowProps): JSX.Element => {
  const styles = useGroupedWorkspaceDashboardStyles();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { workspace, ragSummary, logs } = rowData;
  const detailPanelId = `portfolio-workspace-detail-${workspace.id}`;

  const ragDimensions = [
    ragSummary.overall,
    ragSummary.budget,
    ragSummary.schedule,
    ragSummary.scope,
  ];

  return (
    <div>
      <div
        className={mergeClasses(
          styles.workspaceGrid,
          styles.workspaceRow,
          isExpanded && styles.workspaceRowExpanded,
        )}
      >
        <button
          type="button"
          className={styles.workspaceToggleCell}
          onClick={() => setIsExpanded((current) => !current)}
          aria-expanded={isExpanded}
          aria-controls={detailPanelId}
        >
          <span
            className={mergeClasses(
              styles.chevron,
              isExpanded && styles.chevronExpanded,
            )}
            aria-hidden="true"
          >
            <ChevronDoubleRightRegular />
          </span>
          <span className={styles.workspaceNameGroup}>
            <Tooltip content={workspace.name} relationship="description">
              <span className={styles.workspaceName}>{workspace.name}</span>
            </Tooltip>
            <span className={styles.workspaceClient}>
              {workspace.clientName}
            </span>
          </span>
        </button>

        {ragDimensions.map((dimension) => (
          <div key={dimension.id} className={styles.ragCell}>
            <Tooltip
              content={`${dimension.label}: ${dimension.statusText}`}
              relationship="label"
            >
              <span
                className={mergeClasses(
                  styles.ragDot,
                  getRagDotClassName(styles, dimension.status),
                )}
                role="img"
                tabIndex={0}
              />
            </Tooltip>
          </div>
        ))}

        <div className={styles.countCell}>
          <span className={getCountValueClass(logs.milestones.length, styles)}>
            {logs.milestones.length}
          </span>
        </div>
        <div className={styles.countCell}>
          <span
            className={getCountValueClass(logs.deliverables.length, styles)}
          >
            {logs.deliverables.length}
          </span>
        </div>
        <div className={styles.countCell}>
          <span className={getCountValueClass(logs.raidEntries.length, styles)}>
            {logs.raidEntries.length}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div id={detailPanelId} className={styles.detailRow}>
          <WorkspaceLogTabs logs={logs} />
        </div>
      )}
    </div>
  );
};
